import { ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, Archive, CheckCircle2, FileText, RefreshCw, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { BusinessProcessDetail } from './types'

type Variant = 'action' | 'success' | 'error' | 'terminal'

const VARIANT_STYLES: Record<Variant, { box: string; icon: string; title: string }> = {
  action: { box: 'border-amber-200/70 bg-amber-50/55 dark:border-amber-900 dark:bg-amber-950/25', icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200', title: 'text-foreground' },
  success: { box: 'border-emerald-200/70 bg-emerald-50/55 dark:border-emerald-900 dark:bg-emerald-950/25', icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200', title: 'text-foreground' },
  error: { box: 'border-red-200/70 bg-red-50/45 dark:border-red-900 dark:bg-red-950/20', icon: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200', title: 'text-foreground' },
  terminal: { box: 'border-border/70 bg-muted/35', icon: 'bg-muted text-muted-foreground', title: 'text-foreground' },
}

function post(path: string, data: Record<string, string> = {}) {
  router.post(path, data, { preserveScroll: true })
}

// The single, state-driven "what happens next" panel. It replaces the old flat
// button cluster: it names where the process is and offers only the action the
// current status actually allows, using the same routes/labels as before so the
// controller contract is unchanged. Publish is automatic after approval
// (SZ-1527); retries resume the approved Draft instead of exposing another
// approval or publication action.
export default function ProcessActionPanel({ process }: { process: BusinessProcessDetail }) {
  const id = process.id
  const versionId = process.current_version?.id
  const approveOrResume = () => {
    if (!versionId) return
    post(`/dashboard/business_processes/${id}/approve_draft`, { version_id: versionId })
  }

  let variant: Variant = 'action'
  let icon: ReactNode = <FileText className="size-5" />
  let title = ''
  let description = ''
  let hint: string | null = null
  let primary: ReactNode = null
  let secondary: ReactNode = null

  switch (process.progress_kind) {
    case 'awaiting_approval':
      title = 'Revise e aprove o rascunho'
      description = 'Esta é a confirmação final. Depois da aprovação, o agente prepara e publica o processo automaticamente.'
      if (process.can_approve_draft && versionId) {
        primary = <Button onClick={approveOrResume}><CheckCircle2 className="size-4" />Aprovar rascunho</Button>
      }
      break
    // Estados de progresso não têm cartão: o badge com spinner ao lado do
    // título mostra o andamento, e cancelar/tentar de novo vivem no menu "…"
    // do cabeçalho.
    case 'document_generating':
    case 'skill_generating':
    case 'publication_pending':
      return null
    case 'document_failed':
      variant = 'error'
      icon = <AlertTriangle className="size-5" />
      title = 'Falha ao preparar o processo'
      description = 'O documento não pôde ser preparado. Tente novamente sem precisar aprovar o mesmo rascunho outra vez.'
      if (process.can_retry_draft && versionId) {
        primary = <Button onClick={approveOrResume}><RefreshCw className="size-4" />Tentar novamente</Button>
      }
      break
    case 'skill_pending':
      icon = <RefreshCw className="size-5" />
      title = 'Preparação pausada'
      description = 'O documento aprovado está preservado. Continue a preparação sem revisar nem aprovar o mesmo rascunho outra vez.'
      if (process.can_retry_draft && versionId) {
        primary = <Button onClick={approveOrResume}><RefreshCw className="size-4" />Continuar preparação</Button>
      }
      break
    case 'skill_failed':
      variant = 'error'
      icon = <AlertTriangle className="size-5" />
      title = 'Falha ao preparar o agente'
      description = 'As instruções não puderam ser preparadas. Tente novamente sem refazer o documento nem aprovar outra vez.'
      if (process.can_retry_draft && versionId) {
        primary = <Button onClick={approveOrResume}><RefreshCw className="size-4" />Tentar novamente</Button>
      }
      break
    case 'published':
      // Publicado não precisa de aviso: o badge ao lado do título já diz o
      // estado, e não há ação a oferecer.
      return null
    case 'publication_failed':
      variant = 'error'
      icon = <AlertTriangle className="size-5" />
      title = 'Falha ao disponibilizar o processo ao agente'
      description = 'As instruções foram preparadas, mas a disponibilização automática falhou. Tente novamente.'
      if (process.can_retry_draft && versionId) {
        primary = <Button onClick={approveOrResume}><RefreshCw className="size-4" />Tentar novamente</Button>
      }
      break
    case 'archived':
      variant = 'terminal'
      icon = <Archive className="size-5" />
      title = 'Processo arquivado'
      description = 'Este processo está arquivado e é somente leitura.'
      break
    default:
      return null
  }

  // Cancelling the agent run is available in any active state (matches the
  // previous behavior where "Cancelar" showed alongside the retry action too).
  if (process.can_cancel) {
    secondary = (
      <Button variant="outline" onClick={() => post(`/dashboard/business_processes/${id}/cancel_agent`)}>
        <RotateCcw className="size-4" />Cancelar
      </Button>
    )
  }

  const styles = VARIANT_STYLES[variant]

  return (
    <div className={cn('mb-2 flex flex-col gap-3 rounded-xl border px-3 py-3 sm:flex-row sm:items-center', styles.box)} data-testid="process-action-panel">
      <div className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', styles.icon)}>{icon}</div>
      <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className={cn('text-[15px] font-semibold', styles.title)}>{title}</p>
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</p>
        </div>
        {(primary || secondary || hint) && (
          <div className="mt-3 flex shrink-0 flex-wrap items-center gap-2 sm:mt-0">
            {primary}
            {secondary}
            {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
          </div>
        )}
      </div>
    </div>
  )
}
